import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('casketUrn');
export interface CasketDetails {
  material?: string;
  color?: string;
  style?: string;
  personalization?: string;
  customMade?: boolean;
  specifications?: string;
}

export interface UrnDetails {
  material?: string;
  size?: string;
  design?: string;
  color?: string;
  personalization?: string;
  specifications?: string;
}

export interface OtherDetails {
  arrangementType?: string;
  description?: string;
  specifications?: string;
  location?: string;
  requirements?: string;
}

export type CasketUrnDetails =
  | CasketDetails
  | UrnDetails
  | OtherDetails
  | null;

export interface CasketUrnInfoRec {
  id: string;
  userId: string;
  containerType?: string; // 'casket' | 'urn' | 'other'
  specificType?: string; // For casket: 'metal' | 'wood' | 'fiberglass' | 'bio' | 'alternative' | For urn: 'metal' | 'ceramic' | 'wood' | 'biodegradable' | 'glass' | For other: custom types
  details?: CasketUrnDetails;
  specialInstructions?: string;
  notes?: string;
  estimatedCost?: number;
  purchaseLocation?: string;
  contactName?: string;
  contactPhone?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

class CasketUrnInfoService {
  async getAllCasketUrnInfo(): Promise<CasketUrnInfoRec[]> {
    const response = await http.get('/api/casket-urn');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch casket/urn information');
    }

    return response.data.data || [];
  }

  async getCasketUrnInfoById(id: string): Promise<CasketUrnInfoRec | null> {
    try {
      const response = await http.get(`/api/casket-urn/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch casket/urn information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching casket/urn information by ID:', error);
      return null;
    }
  }

  async createCasketUrnInfo(casketUrnData: Partial<CasketUrnInfoRec>): Promise<CasketUrnInfoRec> {
    const response = await http.post('/api/casket-urn', casketUrnData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save casket/urn information');
    }

    return response.data.data;
  }

  async updateCasketUrnInfo(id: string, casketUrnData: Partial<CasketUrnInfoRec>): Promise<CasketUrnInfoRec> {
    const response = await http.patch(`/api/casket-urn/${id}`, casketUrnData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update casket/urn information');
    }

    return response.data.data;
  }

  async deleteCasketUrnInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/casket-urn/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete casket/urn information');
    }
  }

  async lockCasketUrnInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for casket/urn ID:', id);
    const response = await http.post(`/api/casket-urn/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock casket/urn information');
    }
  }

  async unlockCasketUrnInfo(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for casket/urn ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/casket-urn/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock casket/urn information');
    }
  }

  async getCasketUrnAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/casket-urn/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch casket/urn access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching casket/urn access grants:', error);
      return [];
    }
  }

  async deleteCasketUrnAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/casket-urn/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const casketUrnInfoService = new CasketUrnInfoService();